import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

/**
 * Auto-rotating SKU carousel.
 *
 * variant = "hero"  → full featured (dots, arrows, counter, click-to-zoom lightbox)
 * variant = "card"  → compact thumbnail (small dots, no arrows, no lightbox)
 *
 * Behaviour:
 *   - Pauses auto-rotate on hover (mouseenter → pause, mouseleave → resume)
 *   - Also pauses when lightbox is open
 *   - Keyboard nav inside lightbox: ← / → to change SKU, Esc to close
 */
export const SkuCarousel = ({
  skus,
  accent = "#00FF85",
  intervalMs = 3800,
  testId,
  variant = "hero",
  disableLightbox = false,
}) => {
  // Flatten each SKU's gallery (or single image) into individual slides.
  // Multiple images on one SKU show as separate slides — same code, different photo.
  const slides = useMemo(
    () =>
      skus.flatMap((s) => {
        const imgs = s.gallery && s.gallery.length ? s.gallery : [s.image];
        return imgs.map((src, idx) => ({
          ...s,
          image: src,
          _slideKey: `${s.code}-${idx}`,
          _hasSiblings: imgs.length > 1,
        }));
      }),
    [skus],
  );

  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const count = slides.length;

  const next = useCallback(() => setI((p) => (p + 1) % count), [count]);
  const prev = useCallback(() => setI((p) => (p - 1 + count) % count), [count]);

  // Auto-rotate — paused on hover or when lightbox is open
  useEffect(() => {
    if (count <= 1) return undefined;
    if (paused || lightboxOpen) return undefined;
    const t = setInterval(next, intervalMs);
    return () => clearInterval(t);
  }, [count, intervalMs, paused, lightboxOpen, next]);

  // Keyboard navigation inside lightbox
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxOpen, next, prev]);

  const active = slides[i];
  const isHero = variant === "hero";

  const containerCls = isHero
    ? "relative aspect-[4/3] bg-gradient-to-br from-[#1F2326] via-[#16191C] to-black border border-white/10 overflow-hidden group"
    : "relative aspect-[16/9] bg-gradient-to-br from-[#1F2326] via-[#16191C] to-black overflow-hidden group";

  const imgPadding = isHero ? "p-6" : "p-6";

  return (
    <>
      <div
        data-testid={testId}
        className={containerCls}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {slides.map((s, idx) => (
          <img
            key={s._slideKey}
            src={s.image}
            alt={`${s.code} — ${s.tagline}`}
            className={`absolute inset-0 w-full h-full object-contain ${imgPadding} transition-opacity duration-700 ease-in-out ${
              idx === i ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            loading={idx === 0 ? "eager" : "lazy"}
          />
        ))}

        {/* HERO variant: overlays + arrows + lightbox trigger */}
        {isHero && (
          <>
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
              <div className="bg-black/40 backdrop-blur px-3 py-1.5 border border-white/10">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: accent }}>
                  {active.code}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/70 mt-0.5">
                  {active.tagline}
                </div>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#00FF85] bg-black/40 backdrop-blur px-2 py-1 border border-white/10">
                <span className="inline-block w-1 h-1 rounded-full bg-[#00FF85] mr-1.5 align-middle bbc-pulse" />
                In production
              </span>
            </div>

            {/* Click anywhere on the image to zoom (unless disabled by parent link wrapper) */}
            {!disableLightbox && (
              <button
                type="button"
                aria-label="Open fullscreen"
                onClick={() => setLightboxOpen(true)}
                data-testid={`${testId}-zoom`}
                className="absolute inset-0 z-[1] cursor-zoom-in"
              />
            )}

            {/* Zoom hint badge (top-right on hover) */}
            {!disableLightbox && (
              <span className="absolute top-16 right-4 z-[2] pointer-events-none inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-white/80 bg-black/40 backdrop-blur px-2 py-1 border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn size={10} />
                Click to zoom
              </span>
            )}

            {/* Prev / Next chevrons — hidden if only one SKU */}
            {count > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); prev(); }}
                  data-testid={`${testId}-prev`}
                  className="absolute z-[3] left-3 top-1/2 -translate-y-1/2 w-10 h-10 border border-white/15 bg-black/40 backdrop-blur text-white/80 hover:text-[#00FF85] hover:border-[#00FF85] hover:bg-black/60 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); next(); }}
                  data-testid={`${testId}-next`}
                  className="absolute z-[3] right-3 top-1/2 -translate-y-1/2 w-10 h-10 border border-white/15 bg-black/40 backdrop-blur text-white/80 hover:text-[#00FF85] hover:border-[#00FF85] hover:bg-black/60 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight size={18} />
                </button>
              </>
            )}

            {/* Dots + counter */}
            {count > 1 && (
              <div className="absolute z-[2] bottom-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                <div className="flex items-center gap-1.5 pointer-events-auto">
                  {slides.map((s, idx) => (
                    <button
                      key={s._slideKey}
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setI(idx); }}
                      aria-label={`Show ${s.code}`}
                      data-testid={`${testId}-dot-${idx}`}
                      className={`h-1 transition-all ${idx === i ? "w-6" : "w-3 opacity-40 hover:opacity-80"}`}
                      style={{ background: idx === i ? accent : "#FFFFFF" }}
                    />
                  ))}
                </div>
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/50">
                  {String(i + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
                </span>
              </div>
            )}
          </>
        )}

        {/* CARD variant: minimal top-right SKU code + bottom mini-dots */}
        {!isHero && (
          <>
            <div className="absolute top-3 left-4 right-4 flex items-center justify-between pointer-events-none z-[1]">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/85 bg-black/40 backdrop-blur px-2 py-1 border border-white/10">
                {active.code}
              </span>
            </div>
            {count > 1 && (
              <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between pointer-events-none z-[1]">
                <div className="flex items-center gap-1">
                  {slides.map((s, idx) => (
                    <span
                      key={s._slideKey}
                      className={`h-[3px] transition-all ${idx === i ? "w-4" : "w-2 opacity-40"}`}
                      style={{ background: idx === i ? accent : "#FFFFFF" }}
                    />
                  ))}
                </div>
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">
                  {String(i + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* LIGHTBOX (hero variant only) */}
      {isHero && lightboxOpen && (
        <SkuCarouselLightbox
          skus={slides}
          index={i}
          onIndex={setI}
          onNext={next}
          onPrev={prev}
          onClose={() => setLightboxOpen(false)}
          accent={accent}
          testId={`${testId}-lightbox`}
        />
      )}
    </>
  );
};

const SkuCarouselLightbox = ({ skus, index, onIndex, onNext, onPrev, onClose, accent, testId }) => {
  const active = useMemo(() => skus[index], [skus, index]);
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/92 backdrop-blur-md" onClick={onClose} />

      <div className="relative w-full max-w-6xl aspect-[4/3] bg-gradient-to-br from-[#1F2326] via-[#16191C] to-black border border-[#00FF85]/30 overflow-hidden shadow-2xl shadow-[#00FF85]/10">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#16191C]/80 backdrop-blur">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: accent }}>
              {active.code}
            </div>
            <h3 className="mt-0.5 font-display text-white text-base md:text-lg font-bold tracking-tight">
              {active.name || active.code} — {active.tagline}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid={`${testId}-close`}
            className="w-10 h-10 border border-white/10 hover:border-[#00FF85] text-white/70 hover:text-[#00FF85] flex items-center justify-center transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Image */}
        {skus.map((s, idx) => (
          <img
            key={s._slideKey || s.code}
            src={s.image}
            alt={`${s.code} — ${s.tagline}`}
            className={`absolute inset-0 w-full h-full object-contain p-16 md:p-20 transition-opacity duration-500 ${
              idx === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          />
        ))}

        {/* Prev / Next */}
        {skus.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous"
              onClick={onPrev}
              data-testid={`${testId}-prev`}
              className="absolute z-[3] left-4 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/15 bg-black/40 backdrop-blur text-white hover:text-[#00FF85] hover:border-[#00FF85] hover:bg-black/60 flex items-center justify-center transition"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              aria-label="Next"
              onClick={onNext}
              data-testid={`${testId}-next`}
              className="absolute z-[3] right-4 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/15 bg-black/40 backdrop-blur text-white hover:text-[#00FF85] hover:border-[#00FF85] hover:bg-black/60 flex items-center justify-center transition"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}

        {/* Bottom dots + kbd hints */}
        <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-between px-5 py-4 border-t border-white/5 bg-[#16191C]/80 backdrop-blur">
          <div className="flex items-center gap-2">
            {skus.map((s, idx) => (
              <button
                key={s._slideKey || s.code}
                type="button"
                onClick={() => onIndex(idx)}
                aria-label={`Show ${s.code}`}
                data-testid={`${testId}-dot-${idx}`}
                className={`h-1 transition-all ${idx === index ? "w-8" : "w-4 opacity-40 hover:opacity-80"}`}
                style={{ background: idx === index ? accent : "#FFFFFF" }}
              />
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.22em] text-white/50">
            <span>← / →</span>
            <span>Navigate</span>
            <span className="w-px h-3 bg-white/15" />
            <span>Esc</span>
            <span>Close</span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60">
            {String(index + 1).padStart(2, "0")} / {String(skus.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </div>
  );
};
